import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import MuiTable from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { TableCellProps } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import classNames from "classnames";
import { Avatar } from "components";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useFormatDateHook, useNavigationHooks } from "hooks";
import { startCase } from "lodash";
import { useAccountSelector, useClientIdSelector, useDealIdSelector } from "store";
import { TaskPreview } from "store/types";

import { taskPrioritiesObj, taskStatuses } from "../../../constants";
import { Task } from "../Task/Task";

import "./Table.styles.scss";

const columnsWdith = [24.5, 15, 14, 11.5, 10, 9, 9];

type HeadKeys = keyof Pick<
  TaskPreview,
  "title" | "assignee" | "status" | "priority" | "due_date" | "deal" | "client" | "created_at"
>;

const cellTextStyles: TableCellProps["sx"] = {
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  padding: "0 20px",
  color: "#0a3142",
  fontSize: "16px",
  height: "50px",
  cursor: "pointer"
};

interface HeadCell {
  key: HeadKeys;
  title: string;
}

const headCells: HeadCell[] = [
  {
    key: "title",
    title: "Title"
  },
  {
    key: "deal",
    title: "Deal"
  },
  {
    key: "client",
    title: "Client"
  },
  {
    key: "assignee",
    title: "Assignee"
  },
  {
    key: "status",
    title: "Status"
  },
  {
    key: "priority",
    title: "Priority"
  },
  {
    key: "due_date",
    title: "Due Date"
  },
  {
    key: "created_at",
    title: "Creation Date"
  }
];

type Order = "asc" | "desc";

interface EnhancedTableProps {
  onTitleClick: (key: HeadKeys) => void;
  order: Order;
  orderBy: string;
}

const EnhancedTableHead: React.FC<EnhancedTableProps> = ({ order, orderBy, onTitleClick }) => {
  const dealId = useDealIdSelector();
  const clientId = useClientIdSelector();
  const enableClientCrm = Boolean(useAccountSelector()?.enable_client_crm);

  const updatedCells = useMemo(() => {
    return headCells.filter((item) => {
      // Deal column is redundant inside a deal; Client column only when the CRM flag is on and not inside a client.
      if (item.key === "deal") return !dealId;
      if (item.key === "client") return enableClientCrm && !clientId;
      return true;
    });
  }, [dealId, clientId, enableClientCrm]);

  return (
    <TableHead sx={{ position: "sticky", top: 0, backgroundColor: "white", height: "40px" }}>
      {/* <TableHead> */}
      <TableRow sx={{ backgroundColor: "#edf1f9" }}>
        {updatedCells.map(({ title, key }) => (
          <TableCell
            sx={{ fontSize: "15px", fontWeight: "normal", ...cellTextStyles }}
            key={key}
            align={key === "status" ? "center" : "left"}
            sortDirection={orderBy === key ? order : false}
          >
            <TableSortLabel
              active={orderBy === key}
              direction={orderBy === key ? order : "asc"}
              onClick={() => onTitleClick(key)}
            >
              {startCase(title)}
              {orderBy === key && (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              )}
            </TableSortLabel>
          </TableCell>
        ))}
        {/* <TableCell></TableCell> */}
      </TableRow>
    </TableHead>
  );
};

interface Props {
  refetchTable: () => void;
  tasks: TaskPreview[];
}

export const Table: React.FC<Props> = ({ refetchTable, tasks }) => {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<HeadKeys>("created_at");
  const dealId = useDealIdSelector();
  const clientId = useClientIdSelector();
  const enableClientCrm = Boolean(useAccountSelector()?.enable_client_crm);
  const showClientColumn = enableClientCrm && !clientId;
  const { setParams } = useNavigationHooks();
  const formatDate = useFormatDateHook();

  const [totalWidth, setTotalWidth] = useState<number>(1000);
  const containerRef = useRef<HTMLDivElement>(null);

  const getColumnWidth = useCallback(
    (percentage: number): string => {
      return (totalWidth * percentage) / 100 + "px";
    },
    [totalWidth]
  );

  const onTitleClick = useCallback(
    (key: HeadKeys) => {
      const isAsc = orderBy === key && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(key);
    },
    [order, orderBy]
  );
  useEffect(() => {
    if (containerRef.current) {
      setTotalWidth(containerRef.current.clientWidth);
    }
  }, [containerRef.current?.clientWidth]);
  // Avoid a layout jump when reaching the last page with empty rows.
  // const emptyRows = page > 0 ? Math.max(0, 1 + page - rows.length) : 0;

  return (
    <TableContainer
      ref={containerRef}
      sx={{ borderRadius: "10px", border: "1px solid #dae0e2", borderTop: "none", paddingBottom: "7px", flexGrow: 1 }}
    >
      <MuiTable
        // ref={tableRef}
        sx={{
          minWidth: 900,
          borderRadius: "10px 20px 30px 40px",
          color: "#0a3142"
          // tableLayout: "fixed",
        }}
        aria-labelledby="tableTitle"
      >
        <EnhancedTableHead order={order} orderBy={orderBy} onTitleClick={onTitleClick} />
        <TableBody>
          {sortTable(tasks, getComparator(order, orderBy)).map((row: TaskPreview) => {
            const { id, title, assignee, status, priority, due_date, deal, client, is_overdue, created_at } = row;
            return (
              <TableRow
                sx={{
                  ":hover": { backgroundColor: "#f5f7f7" }
                }}
                key={id}
                onClick={() => setParams({ search: { task: id } })}
              >
                <TableCell
                  sx={{
                    fontSize: "16px",
                    // cursor: "pointer",
                    width: getColumnWidth(columnsWdith[0]),
                    maxWidth: getColumnWidth(columnsWdith[0]),
                    ...cellTextStyles
                  }}
                  align="left"
                >
                  <div className="cell-tooltip">
                    <MaterialTooltip placement="top" title={title}>
                      <Typography noWrap>{title}</Typography>
                    </MaterialTooltip>
                  </div>
                </TableCell>
                {!dealId && (
                  <TableCell
                    sx={{
                      fontSize: "16px",
                      // width: getColumnWidth(columnsWdith[1]),
                      // maxWidth: getColumnWidth(columnsWdith[1]),
                      width: "180px",
                      maxWidth: "180px",
                      ...cellTextStyles
                    }}
                    align="left"
                  >
                    <div className="cell-tooltip">
                      <MaterialTooltip placement="top" title={deal?.title || ""}>
                        <Typography noWrap>{deal?.title}</Typography>
                      </MaterialTooltip>
                    </div>
                  </TableCell>
                )}
                {showClientColumn && (
                  <TableCell
                    sx={{
                      fontSize: "16px",
                      width: "180px",
                      maxWidth: "180px",
                      ...cellTextStyles
                    }}
                    align="left"
                  >
                    <div className="cell-tooltip">
                      <MaterialTooltip placement="top" title={client?.name || ""}>
                        <Typography noWrap>{client?.name}</Typography>
                      </MaterialTooltip>
                    </div>
                  </TableCell>
                )}
                <TableCell
                  align="left"
                  sx={{
                    // width: getColumnWidth(columnsWdith[2]),
                    // maxWidth: getColumnWidth(columnsWdith[3]),
                    width: "180px",
                    maxWidth: "180px",
                    ...cellTextStyles
                  }}
                >
                  {assignee && (
                    <div className="assign-box">
                      <Avatar size={23} src={assignee.profile_photo_url || ""} />
                      <MaterialTooltip placement="top" title={assignee.full_name}>
                        <Typography noWrap style={{ fontSize: "16px" }}>
                          {assignee.full_name}
                        </Typography>
                      </MaterialTooltip>
                    </div>
                  )}
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    // width: getColumnWidth(columnsWdith[3]),
                    // maxWidth: getColumnWidth(columnsWdith[3]),
                    width: "136px",
                    maxWidth: "136px",
                    ...cellTextStyles
                  }}
                  // align="center"
                >
                  {status && (
                    <div className={classNames("box", "status-box", `status-box--${status}`)}>
                      {startCase(taskStatuses[status])}
                    </div>
                  )}
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    // width: getColumnWidth(columnsWdith[4]),
                    // maxWidth: getColumnWidth(columnsWdith[4]),
                    width: "120px",
                    maxWidth: "120px",
                    ...cellTextStyles
                  }}
                  // align="center"
                >
                  <div className={classNames("box", "priority-box", `priority-box--${priority}`)}>
                    <div className={classNames("priority-box__square", `priority-box__square--${priority}`)}></div>
                    <span>{priority && taskPrioritiesObj[priority]}</span>
                  </div>
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    // width: getColumnWidth(columnsWdith[5]),
                    // maxWidth: getColumnWidth(columnsWdith[5]),
                    width: "125px",
                    maxWidth: "125px",
                    color: is_overdue ? "#ff0000" : "#0a3142",
                    ...cellTextStyles
                  }}
                  // align="center"
                >
                  {formatDate({ date: due_date })}
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    // width: getColumnWidth(columnsWdith[6]),
                    width: "155px",
                    maxWidth: "155px",
                    ...cellTextStyles
                  }}
                >
                  {formatDate({ date: created_at })}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </MuiTable>
      <Task refetchTable={refetchTable} />
    </TableContainer>
  );
};

type Competitor = (a: TaskPreview, b: TaskPreview) => number;

const sortTable = (array: readonly TaskPreview[], comparator: (a: TaskPreview, b: TaskPreview) => number) => {
  const stabilizedThis = array.map((el, index) => [el, index] as [TaskPreview, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

const getComparator = (order: Order, orderBy: HeadKeys): Competitor => {
  return order === "desc"
    ? (a: TaskPreview, b: TaskPreview) => descendingComparator(a, b, orderBy)
    : (a: TaskPreview, b: TaskPreview) => -descendingComparator(a, b, orderBy);
};

const descendingComparator = (a: TaskPreview, b: TaskPreview, orderBy: HeadKeys): number => {
  if (orderBy === "assignee") {
    return primitiveOrder(a.assignee?.id || 0, b.assignee?.id || 0);
  }

  if (orderBy === "deal") {
    return primitiveOrder(a.deal?.id || 0, b.deal?.id || 0);
  }

  if (orderBy === "client") {
    return primitiveOrder(a.client?.id || 0, b.client?.id || 0);
  }

  const primitiveA = a[orderBy] || "";
  const primitiveb = b[orderBy] || "";

  if (primitiveA < primitiveb) {
    return -1;
  }
  if (primitiveA > primitiveb) {
    return 1;
  }
  return 0;
};

const primitiveOrder = (a: string | number, b: string | number): number => {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
};
